import React, { useState, useMemo, useRef, useEffect } from 'react'
import { Brain, Check, ChevronDown, ChevronUp, EllipsisVertical, FolderInput, Library, Pencil, Search, TriangleAlert, X } from 'lucide-react'
import type { PipelineQuestion, QuestionStatus } from '../../hooks/usePipelineEngine'
import katex from 'katex'
import { repairMathControlChars } from '../../pages/Exams/generate/MathText'
import 'katex/dist/katex.min.css'
import DOMPurify from 'dompurify'

const renderLatexInHtml = (html: string) => {
    if (!html) return ''
    try {
        let out = String(html)
        out = out.replace(/\$\$([\s\S]+?)\$\$/g, (_m, expr) => {
            try { return katex.renderToString(repairMathControlChars(expr), { displayMode: true, throwOnError: false }) } catch { return _m }
        })
        out = out.replace(/\$(?!\$)([^$]+?)\$/g, (_m, expr) => {
            try { return katex.renderToString(repairMathControlChars(expr), { displayMode: false, throwOnError: false }) } catch { return _m }
        })
        return DOMPurify.sanitize(out)
    } catch { return DOMPurify.sanitize(String(html)) }
}

const stripHtml = (html: string) => html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()

const statusConfig: Record<QuestionStatus, { icon: React.ReactNode; color: string; label: string }> = {
    pending: { icon: <div className="h-2 w-2 rounded-full bg-line-strong motion-safe:animate-pulse" />, color: 'text-muted', label: 'Pending' },
    chapter_done: { icon: <div className="h-2 w-2 rounded-full bg-slate motion-safe:animate-pulse" />, color: 'text-slate', label: 'Chapters assigned' },
    rag_done: { icon: <div className="h-2 w-2 rounded-full bg-navy motion-safe:animate-pulse dark:bg-mist-fixed" />, color: 'text-muted-2', label: 'RAG complete' },
    explanation_done: { icon: <Check size={16} strokeWidth={2} aria-hidden className="text-success" />, color: 'text-success', label: 'Complete' },
    failed: { icon: <X size={16} strokeWidth={2} aria-hidden className="text-danger" />, color: 'text-danger-ink', label: 'Failed' },
    no_context: { icon: <TriangleAlert size={16} strokeWidth={1.8} aria-hidden className="text-warning-ink" />, color: 'text-warning-ink', label: 'No context' },
}

interface Props {
    q: PipelineQuestion
    index: number
    onSearchAllBooks?: (index: number) => void
    onSearchSpecificBooks?: (index: number) => void
    onUseAIKnowledge?: (index: number) => void
    onReassignChapter?: (index: number) => void
    /** Chapters the question may be moved to (the processing catalog). */
    catalog?: any[]
    /** Manual edits after processing; the editor shows only when both are given. */
    onSetChapter?: (index: number, entry: any) => void
    onEditMeta?: (index: number, patch: { explanation?: string; references?: string[] }) => void
}

const catalogKey = (c: { id: unknown; is_subchapter?: boolean }) => `${c.is_subchapter ? 's' : 'c'}:${c.id}`

const fieldCls = 'w-full rounded-[10px] border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-navy focus:ring-2 focus:ring-accent/40'

const CollapsedQuestionCard: React.FC<Props> = ({ q, index, onSearchAllBooks, onSearchSpecificBooks, onUseAIKnowledge, onReassignChapter, catalog, onSetChapter, onEditMeta }) => {
    const [expanded, setExpanded] = useState(false)
    const [editing, setEditing] = useState(false)
    const [draftChapter, setDraftChapter] = useState('')
    const [draftExplanation, setDraftExplanation] = useState('')
    const [draftReferences, setDraftReferences] = useState('')
    const canEdit = !!onEditMeta && !!onSetChapter

    const startEdit = () => {
        const ca = q._chapterAssignment
        setDraftChapter(ca ? catalogKey({ id: ca.chaporsubid, is_subchapter: ca.is_subchapter }) : '')
        setDraftExplanation(String(q.explanation ?? ''))
        setDraftReferences((Array.isArray(q.references) ? q.references : q.references ? [q.references] : []).join('\n'))
        setEditing(true)
    }

    const saveEdit = () => {
        const ca = q._chapterAssignment
        const currentKey = ca ? catalogKey({ id: ca.chaporsubid, is_subchapter: ca.is_subchapter }) : ''
        if (draftChapter && draftChapter !== currentKey) {
            const entry = (catalog ?? []).find((c) => catalogKey(c) === draftChapter)
            if (entry) onSetChapter!(index, entry)
        }
        onEditMeta!(index, {
            explanation: draftExplanation.trim(),
            references: draftReferences.split('\n').map((r) => r.trim()).filter(Boolean),
        })
        setEditing(false)
    }
    const [menuOpen, setMenuOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const status = statusConfig[q._status] || statusConfig.pending

    const questionPreview = useMemo(() => {
        const plain = stripHtml(q.question || '')
        return plain.length > 120 ? plain.slice(0, 120) + '…' : plain
    }, [q.question])

    const chapterLabel = useMemo(() => {
        const ca = q._chapterAssignment
        if (!ca) return null
        if (ca.subchapter_name) return `${ca.chapter_name} › ${ca.subchapter_name}`
        return ca.chapter_name
    }, [q._chapterAssignment])

    const renderedExplanation = useMemo(() => q.explanation ? renderLatexInHtml(String(q.explanation)) : null, [q.explanation])

    const isProcessing = !!q._processing
    const isSucceeded = q._status === 'explanation_done'
    const isNoContext = q._status === 'no_context'
    const isFailed = q._status === 'failed'
    const hasActions = onSearchAllBooks || onSearchSpecificBooks || onUseAIKnowledge || onReassignChapter

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
        }
        if (menuOpen) document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [menuOpen])

    const choicesPreview = useMemo(() => {
        if (!Array.isArray(q.choices) || q.choices.length === 0) return null
        return q.choices.map((c: any, ci: number) => {
            const label = String.fromCharCode(65 + ci)
            return `${label}. ${stripHtml(String(c))}`
        }).join('  |  ')
    }, [q.choices])

    const ActionMenu = () => (
        <div role="menu" className="absolute right-0 top-full z-50 mt-1 min-w-[210px] rounded-[12px] border border-line bg-surface py-1 shadow-card">
            {onSearchAllBooks && (
                <button type="button" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onSearchAllBooks(index) }}
                    role="menuitem" className="flex w-full items-center gap-2 px-4 py-2 text-left text-xs font-medium text-ink hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none">
                    <Search size={14} strokeWidth={1.8} aria-hidden /> Search all books
                </button>
            )}
            {onSearchSpecificBooks && (
                <button type="button" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onSearchSpecificBooks(index) }}
                    role="menuitem" className="flex w-full items-center gap-2 px-4 py-2 text-left text-xs font-medium text-ink hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none">
                    <Library size={14} strokeWidth={1.8} aria-hidden /> Search specific books
                </button>
            )}
            {onUseAIKnowledge && (
                <button type="button" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onUseAIKnowledge(index) }}
                    role="menuitem" className="flex w-full items-center gap-2 px-4 py-2 text-left text-xs font-medium text-ink hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none">
                    <Brain size={14} strokeWidth={1.8} aria-hidden /> Use AI knowledge
                </button>
            )}
            {onReassignChapter && (
                <button type="button" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onReassignChapter(index) }}
                    role="menuitem" className="flex w-full items-center gap-2 px-4 py-2 text-left text-xs font-medium text-ink hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none">
                    <FolderInput size={14} strokeWidth={1.8} aria-hidden /> Reassign chapter
                </button>
            )}
        </div>
    )

    return (
        <div className={`overflow-hidden rounded-[14px] border bg-surface transition-all ${isProcessing ? 'border-accent ring-1 ring-accent/30' : 'border-line'}`}>
            <div className="flex items-center">
                <button
                    type="button"
                    onClick={() => setExpanded(!expanded)}
                    aria-expanded={expanded}
                    className="flex min-w-0 flex-1 items-center gap-3 px-3.5 py-3 text-left transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
                >
                    <span className="grid h-[30px] w-[30px] flex-none place-items-center rounded-[10px] bg-track font-mono text-xs font-semibold text-muted-2">
                        {index + 1}
                    </span>
                    <span className="flex-shrink-0">{isProcessing ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-accent border-t-transparent" /> : status.icon}</span>
                    <div className="flex-1 min-w-0">
                        <span className="block truncate text-sm font-medium text-ink">
                            {questionPreview}
                        </span>
                        {choicesPreview && (
                            <span className="mt-0.5 block truncate text-xs text-muted">
                                {choicesPreview}
                            </span>
                        )}
                    </div>
                    {chapterLabel && (
                        <span className="hidden max-w-[200px] truncate rounded-full bg-mist px-2.5 py-0.5 text-xs font-semibold text-muted-2 sm:inline-block">
                            {chapterLabel}
                        </span>
                    )}
                    <span className={`flex-shrink-0 text-xs font-semibold ${isProcessing ? 'text-accent-ink' : status.color}`}>{isProcessing ? 'Processing...' : status.label}</span>
                    {expanded ? <ChevronUp size={16} strokeWidth={1.8} aria-hidden className="flex-shrink-0 text-muted" /> : <ChevronDown size={16} strokeWidth={1.8} aria-hidden className="flex-shrink-0 text-muted" />}
                </button>

                {/* Action menu - 3-dot for succeeded, prominent buttons for no_context/failed */}
                {hasActions && !isProcessing && (
                    <div className="relative flex-shrink-0 pr-3" ref={menuRef}>
                        {isSucceeded ? (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen) }}
                                className="grid h-9 w-9 place-items-center rounded-[10px] text-muted transition-colors hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                title="More actions"
                                aria-label={`More actions for question ${index + 1}`}
                                aria-haspopup="menu"
                                aria-expanded={menuOpen}
                            >
                                <EllipsisVertical size={18} strokeWidth={1.8} aria-hidden />
                            </button>
                        ) : (isNoContext || isFailed) ? (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen) }}
                                aria-haspopup="menu"
                                aria-expanded={menuOpen}
                                className="inline-flex min-h-[34px] items-center gap-1 rounded-[10px] bg-warning-bg px-[11px] text-xs font-semibold text-warning-ink transition hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                            >
                                Actions <ChevronDown size={14} strokeWidth={1.8} aria-hidden />
                            </button>
                        ) : null}
                        {menuOpen && <ActionMenu />}
                    </div>
                )}
            </div>

            {expanded && (
                <div className="space-y-3 border-t border-hairline px-4 pb-4">
                    <div className="pt-3">
                        <div className="mb-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted">Question</div>
                        <div
                            className="prose prose-sm max-w-none text-sm text-ink dark:prose-invert"
                            dangerouslySetInnerHTML={{ __html: renderLatexInHtml(q.question || '') }}
                        />
                    </div>

                    {Array.isArray(q.choices) && q.choices.length > 0 && (
                        <div>
                            <div className="mb-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted">Choices</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                                {q.choices.map((c: any, ci: number) => {
                                    const label = String.fromCharCode(65 + ci)
                                    const ans = (q.answer ?? q.ans ?? q.correct_ans ?? '').toString().trim().toUpperCase()
                                    const isCorrect = ans === label || ans === String(ci + 1)
                                    return (
                                        <div key={ci} className={`rounded-[8px] px-2 py-1 text-xs ${isCorrect ? 'bg-success-bg font-semibold text-success' : 'text-muted-2'}`}>
                                            <span className="font-bold mr-1">{label}.</span> {stripHtml(String(c))}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}

                    {canEdit && !editing && (
                        <div className="flex justify-end">
                            <button type="button" onClick={startEdit}
                                className="inline-flex items-center gap-1.5 rounded-[10px] border border-line-strong px-2.5 py-1.5 text-xs font-semibold text-ink hover:border-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                                <Pencil size={13} aria-hidden /> Edit chapter, explanation and references
                            </button>
                        </div>
                    )}

                    {editing && (
                        <div className="space-y-3 rounded-[12px] border border-navy/30 bg-surface-2 p-3" aria-label={`Edit question ${index + 1}`} role="group">
                            <label className="block">
                                <span className="mb-1 block text-xs font-semibold text-ink">Chapter</span>
                                <select value={draftChapter} onChange={(e) => setDraftChapter(e.target.value)} className={fieldCls}>
                                    {!draftChapter && <option value="">No chapter</option>}
                                    {(catalog ?? []).map((c) => (
                                        <option key={catalogKey(c)} value={catalogKey(c)}>{c.is_subchapter ? `  ${c.name}` : c.name}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="block">
                                <span className="mb-1 block text-xs font-semibold text-ink">Explanation</span>
                                <textarea value={draftExplanation} onChange={(e) => setDraftExplanation(e.target.value)} rows={5} className={fieldCls} />
                            </label>
                            <label className="block">
                                <span className="mb-1 block text-xs font-semibold text-ink">References <span className="font-normal text-muted">(one per line)</span></span>
                                <textarea value={draftReferences} onChange={(e) => setDraftReferences(e.target.value)} rows={3} className={fieldCls} />
                            </label>
                            <div className="flex justify-end gap-2">
                                <button type="button" onClick={() => setEditing(false)} className="rounded-[10px] px-3 py-1.5 text-xs font-semibold text-muted-2 hover:bg-surface">Cancel</button>
                                <button type="button" onClick={saveEdit} className="rounded-[10px] bg-navy px-3 py-1.5 text-xs font-semibold text-white hover:brightness-110">Save changes</button>
                            </div>
                        </div>
                    )}

                    {!editing && chapterLabel && (
                        <div>
                            <div className="mb-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted">Chapter</div>
                            <div className="text-sm text-ink">{chapterLabel}</div>
                        </div>
                    )}

                    {!editing && renderedExplanation && (
                        <div>
                            <div className="mb-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted">Explanation</div>
                            <div
                                className="prose prose-sm max-w-none rounded-[12px] bg-surface-2 p-3 text-sm text-ink dark:prose-invert"
                                dangerouslySetInnerHTML={{ __html: renderedExplanation }}
                            />
                        </div>
                    )}

                    {!editing && Array.isArray(q.references) && q.references.length > 0 && (
                        <div>
                            <div className="mb-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted">References ({q.references.length})</div>
                            <div className="space-y-1">
                                {q.references.map((ref: string, ri: number) => (
                                    <div key={ri} className="rounded-[8px] bg-surface-2 px-2 py-1 text-xs text-muted-2" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(ref) }} />
                                ))}
                            </div>
                        </div>
                    )}

                    {q._error && (
                        <div className="rounded-[10px] bg-danger-bg px-3 py-2 text-xs text-danger-ink">
                            {q._error}
                        </div>
                    )}

                    {q.corrected_answer && (
                        <div className="text-xs">
                            <span className="font-semibold text-warning-ink">Corrected answer:</span> {q.corrected_answer}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default React.memo(CollapsedQuestionCard)
